import { IDynamicSnippet, ExecuteSnippetResult } from "../types/pageBuilder.types";
import { AxiosInstance } from "axios";

// Export a hook-based wrapper for convenience
import { useApi } from "../context/ApiContext";

// This service should be used with api instance passed from component
export const createDynamicSnippetService = (api: AxiosInstance) => ({
  /**
   * Fetch all snippets for the current store
   */
  async getSnippets(options?: {
    dataSource?: string;
    page?: number;
    limit?: number;
  }): Promise<{ snippets: IDynamicSnippet[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.dataSource) params.append("dataSource", options.dataSource);
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());

    const response = await api.get(`/page-builder/snippets?${params.toString()}`);
    return response.data;
  },

  /**
   * Get a single snippet by ID
   */
  async getSnippet(snippetId: string): Promise<IDynamicSnippet> {
    const response = await api.get(`/page-builder/snippets/${snippetId}`);
    return response.data.snippet;
  },

  /**
   * Create a new snippet
   */
  async createSnippet(snippet: Omit<IDynamicSnippet, "_id" | "storeId" | "createdAt" | "updatedAt">): Promise<IDynamicSnippet> {
    const response = await api.post("/page-builder/snippets", snippet);
    return response.data.snippet;
  },

  /**
   * Update a snippet
   */
  async updateSnippet(snippetId: string, updates: Partial<IDynamicSnippet>): Promise<IDynamicSnippet> {
    const response = await api.put(`/page-builder/snippets/${snippetId}`, updates);
    return response.data.snippet;
  },

  /**
   * Delete a snippet
   */
  async deleteSnippet(snippetId: string): Promise<void> {
    await api.delete(`/page-builder/snippets/${snippetId}`);
  },

  /**
   * Execute a snippet (fetch data)
   */
  async executeSnippet(snippetId: string, options?: {
    page?: number;
    limit?: number;
  }): Promise<ExecuteSnippetResult> {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());

    const response = await api.get(`/page-builder/snippets/${snippetId}/execute?${params.toString()}`);
    return response.data;
  },

  /**
   * Execute a data source directly (for loop components)
   */
  async executeDataSource(dataSource: string, filters: any = {}, options?: {
    page?: number;
    limit?: number;
    storeId?: string;
  }): Promise<ExecuteSnippetResult> {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());

    // dataSource must be in the request body, not query params
    const body = { dataSource, filters };
    const response = await api.post(`/page-builder/data-source/execute?${params.toString()}`, body);
    return response.data;
  },

  /**
   * Execute a data source publicly (for page rendering, with caching)
   */
  async executeDataSourcePublic(dataSource: string, filters: any = {}, options?: {
    page?: number;
    limit?: number;
    storeId?: string;
  }): Promise<ExecuteSnippetResult> {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", options.page.toString());
    if (options?.limit) params.append("limit", options.limit.toString());

    const body: any = { dataSource, filters };
    if (options?.storeId) {
      body.storeId = options.storeId;
    }

    const response = await api.post(`/page-builder/data-source/execute-public?${params.toString()}`, body);
    return response.data;
  },
});

export const useDynamicSnippetService = () => {
  const { api } = useApi();
  return createDynamicSnippetService(api);
};

// For backward compatibility, export a default instance (will need api from context)
export const dynamicSnippetService = {
  getSnippets: async (_options?: any) => {
    throw new Error("Use useDynamicSnippetService hook instead");
  },
  getSnippet: async (_snippetId: string) => {
    throw new Error("Use useDynamicSnippetService hook instead");
  },
  createSnippet: async (_snippet: any) => {
    throw new Error("Use useDynamicSnippetService hook instead");
  },
  updateSnippet: async (_snippetId: string, _updates: any) => {
    throw new Error("Use useDynamicSnippetService hook instead");
  },
  deleteSnippet: async (_snippetId: string) => {
    throw new Error("Use useDynamicSnippetService hook instead");
  },
  executeSnippet: async (_snippetId: string, _options?: any) => {
    throw new Error("Use useDynamicSnippetService hook instead");
  },
};

